/**
 * followTooltipDirective – How to use
 *
 * Register the directive (globally or locally):
 *
 * // Global (e.g., main.ts)
 * import { createApp } from 'vue'
 * import App from './App.vue'
 * import followTooltip from '@/components/shared/followTooltipDirective'
 *
 * const app = createApp(App)
 * app.directive('follow-tooltip', followTooltip)
 * app.mount('#app')
 *
 * // Local (inside a component)
 * export default {
 *   directives: { 'follow-tooltip': followTooltip },
 * }
 *
 * Use it in any template:
 *
 * <section
 *   v-follow-tooltip="{
 *     content: 'Purchase available for INE subscribers only.',
 *     placement: 'top-right',       // 'top' | 'bottom' | 'left' | 'right' (+ optional '-left'/'-right'/'-top'/'-bottom')
 *     follow: true,                 // follow mouse (true) or anchor to element (false)
 *     classes: 'my-custom-class',   // extra CSS classes for the tooltip root
 *     html: false,                  // allow HTML content (dangerous if untrusted)
 *     offset: 10,                   // px gap from cursor/element
 *     autoFlip: true,               // auto flip when tooltip would overflow viewport
 *     enabled: true,                // quick toggle on/off
 *     ignoreSelector: '.ignore-tooltip' // hide/avoid showing when event target matches this selector
 *   }"
 * >
 *   Hover me
 * </section>
 *
 * Notes:
 * - Pass `null`/`undefined`/`{ enabled: false }` to disable the tooltip.
 * - When `follow` is true, the tooltip tracks the cursor; otherwise it anchors around the element using `placement`.
 * - `placement` supports compound values like `top-right`, `bottom-left`, etc.; auto-flip uses the primary direction.
 * - Style hooks: `.v-follow-tooltip`, `.v-follow-tooltip-content`, `.v-follow-tooltip-arrow` (all applied to the tooltip.)
 *
 * Example component showing all options:
 *
 * <template>
 *   <button
 *     class="btn"
 *     v-follow-tooltip="tooltipOptions"
 *   >
 *     Action
 *   </button>
 * </template>
 *
 * <script setup lang="ts">
 * import { ref } from 'vue'
 *
 * const tooltipOptions = ref({
 *   content: '<strong>Heads up!</strong> Read this.',
 *   placement: 'bottom-left',
 *   follow: true,
 *   classes: 'tooltip--brand',
 *   html: true,
 *   offset: 12,
 *   autoFlip: true,
 *   enabled: true,
 *   ignoreSelector: '.ignore-tooltip',
 * })
 * </script>
 */
import { ObjectDirective } from 'vue'

interface TooltipConfig {
  content: string
  // Allow compound placements as strings (e.g. "top-right")
  placement: string
  follow: boolean
  classes: string
  html: boolean
  offset: number
  autoFlip: boolean
  enabled: boolean
  ignoreSelector?: string | null
}

interface ElWithTooltip extends HTMLElement {
  _followTooltip?: {
    tooltip: HTMLElement
    content: HTMLElement
    move: (e: MouseEvent) => void
    show: (e: MouseEvent) => void
    hide: (e?: MouseEvent) => void
    config: TooltipConfig
  }
}

// --------------------------------------------------------------------------
// Throttle Helper Function
// --------------------------------------------------------------------------
function throttle(fn: (...args: any[]) => void, wait: number) {
  let inThrottle = false
  return function throttled(this: any, ...args: any[]) {
    if (!inThrottle) {
      fn.apply(this, args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, wait)
    }
  }
}

// --------------------------------------------------------------------------
// Helper Functions
// --------------------------------------------------------------------------

// Determines if the event should be ignored based on the ignoreSelector.
function shouldIgnoreEvent(e: MouseEvent, ignoreSelector?: string | null): boolean {
  if (!ignoreSelector) return false
  if (e.target instanceof Element) {
    return !!e.target.closest(ignoreSelector)
  }
  return false
}

// Update the tooltip's content based on whether HTML mode is enabled.
function applyContent(el: HTMLElement, config: TooltipConfig) {
  if (config.html) {
    el.innerHTML = config.content
  } else {
    el.textContent = config.content
  }
}

// Normalize the provided value into a TooltipConfig.
// If the value is null or undefined, enabled will be false.
function normalize(value: any): TooltipConfig {
  const offset = value?.offset ?? 10
  if (value == null) {
    return {
      content: '',
      placement: 'top',
      follow: false,
      classes: '',
      html: false,
      offset,
      autoFlip: true,
      enabled: false,
      ignoreSelector: null,
    }
  }
  return {
    content: value.content || '',
    placement: value.placement || 'top',
    follow: value.follow !== false,
    classes: value.classes || '',
    html: value.html === true,
    offset,
    autoFlip: value.autoFlip !== false,
    enabled: true,
    ignoreSelector: value.ignoreSelector || null,
  }
}

// Base styling for the tooltip element.
function baseTooltipStyle(): Partial<CSSStyleDeclaration> {
  return {
    position: 'fixed',
    display: 'none',
    pointerEvents: 'none',
    zIndex: '9999',
    background: '#57575c',
    color: 'white',
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: '14px',
    lineHeight: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    transition: 'opacity 0.2s ease',
    opacity: '0',
    maxWidth: '250px',
  }
}

// Determine effective placement for auto-flip.
// Compound placements (e.g. "top-right") are split; auto-flip logic uses the primary value.
function getEffectivePlacement(
  source: MouseEvent | DOMRect,
  tooltip: HTMLElement,
  config: TooltipConfig
) {
  if (!config.autoFlip) return config.placement

  const margin = 10
  const vw = window.innerWidth
  const vh = window.innerHeight
  const parts = config.placement.split('-')
  const primary = parts[0]
  let effectivePrimary = primary

  const pos =
    'clientX' in source
      ? { x: source.clientX, y: source.clientY }
      : { x: source.left + source.width / 2, y: source.top + source.height / 2 }

  switch (primary) {
    case 'top':
      if (pos.y - tooltip.offsetHeight - config.offset < margin) effectivePrimary = 'bottom'
      break
    case 'bottom':
      if (pos.y + tooltip.offsetHeight + config.offset > vh - margin) effectivePrimary = 'top'
      break
    case 'left':
      if (pos.x - tooltip.offsetWidth - config.offset < margin) effectivePrimary = 'right'
      break
    case 'right':
      if (pos.x + tooltip.offsetWidth + config.offset > vw - margin) effectivePrimary = 'left'
      break
  }

  const secondary = parts[1] ? parts[1] : 'center'
  return effectivePrimary + (secondary !== 'center' ? `-${secondary}` : '')
}

// Calculate the (x, y) position for the tooltip based on the source, placement, and offset.
// Supports compound placements.
function calculatePosition(
  source: MouseEvent | DOMRect,
  tooltip: HTMLElement,
  config: TooltipConfig
) {
  let offset = config.offset
  let x = 0
  let y = 0

  const effectivePlacement = getEffectivePlacement(source, tooltip, config)
  const parts = effectivePlacement.split('-')
  const primary = parts[0]
  const secondary = parts[1] || 'center'

  let sourceX = 0
  let sourceY = 0
  if ('clientX' in source) {
    sourceX = source.clientX
    sourceY = source.clientY
  } else {
    sourceX = source.left + source.width / 2
    sourceY = source.top + source.height / 2
  }

  if (primary === 'top') {
    y = sourceY - tooltip.offsetHeight - offset
    if (secondary === 'center') {
      x = sourceX - tooltip.offsetWidth / 2
    } else if (secondary === 'right') {
      x = sourceX - tooltip.offsetWidth + 15
    } else if (secondary === 'left') {
      x = sourceX - 15
    }
  } else if (primary === 'bottom') {
    offset += 15
    y = sourceY + offset
    if (secondary === 'center') {
      x = sourceX - tooltip.offsetWidth / 2
    } else if (secondary === 'right') {
      x = sourceX - tooltip.offsetWidth + 15
    } else if (secondary === 'left') {
      x = sourceX - 15
    }
  } else if (primary === 'left') {
    x = sourceX - tooltip.offsetWidth - offset
    if (secondary === 'center') {
      y = sourceY - tooltip.offsetHeight / 2
    } else if (secondary === 'top') {
      y = sourceY
    } else if (secondary === 'bottom') {
      y = sourceY - tooltip.offsetHeight
    }
  } else if (primary === 'right') {
    x = sourceX + offset + 5
    if (secondary === 'center') {
      y = sourceY - tooltip.offsetHeight / 2
    } else if (secondary === 'top') {
      y = sourceY
    } else if (secondary === 'bottom') {
      y = sourceY - tooltip.offsetHeight
    }
  }

  return { x, y }
}

// Update the arrow element's style and position based on the effective placement.
function updateArrow(tooltip: HTMLElement, effectivePlacement: string) {
  const arrow = tooltip.querySelector('.v-follow-tooltip-arrow') as HTMLElement
  const arrowColor = '#57575c'
  if (!arrow) return

  // Reset arrow style
  arrow.style.position = 'absolute'
  arrow.style.width = '0'
  arrow.style.height = '0'
  arrow.style.border = 'none'
  arrow.style.top = ''
  arrow.style.bottom = ''
  arrow.style.left = ''
  arrow.style.right = ''

  const parts = effectivePlacement.split('-')
  const primary = parts[0]
  const secondary = parts[1] || 'center'
  const arrowSize = 6

  if (primary === 'top') {
    arrow.style.bottom = `-${arrowSize}px`
    if (secondary === 'center') {
      arrow.style.left = `calc(50% - ${arrowSize}px)`
    } else if (secondary === 'left') {
      arrow.style.left = `10px`
    } else if (secondary === 'right') {
      arrow.style.right = `10px`
    }
    arrow.style.borderLeft = `${arrowSize}px solid transparent`
    arrow.style.borderRight = `${arrowSize}px solid transparent`
    arrow.style.borderTop = `${arrowSize}px solid ${arrowColor}`
  } else if (primary === 'bottom') {
    arrow.style.top = `-${arrowSize}px`
    if (secondary === 'center') {
      arrow.style.left = `calc(50% - ${arrowSize}px)`
    } else if (secondary === 'left') {
      arrow.style.left = `10px`
    } else if (secondary === 'right') {
      arrow.style.right = `10px`
    }
    arrow.style.borderLeft = `${arrowSize}px solid transparent`
    arrow.style.borderRight = `${arrowSize}px solid transparent`
    arrow.style.borderBottom = `${arrowSize}px solid ${arrowColor}`
  } else if (primary === 'left') {
    arrow.style.right = `-${arrowSize}px`
    if (secondary === 'center') {
      arrow.style.top = `calc(50% - ${arrowSize}px)`
    } else if (secondary === 'top') {
      arrow.style.top = `10px`
    } else if (secondary === 'bottom') {
      arrow.style.bottom = `10px`
    }
    arrow.style.borderTop = `${arrowSize}px solid transparent`
    arrow.style.borderBottom = `${arrowSize}px solid transparent`
    arrow.style.borderLeft = `${arrowSize}px solid ${arrowColor}`
  } else if (primary === 'right') {
    arrow.style.left = `-${arrowSize}px`
    if (secondary === 'center') {
      arrow.style.top = `calc(50% - ${arrowSize}px)`
    } else if (secondary === 'top') {
      arrow.style.top = `10px`
    } else if (secondary === 'bottom') {
      arrow.style.bottom = `10px`
    }
    arrow.style.borderTop = `${arrowSize}px solid transparent`
    arrow.style.borderBottom = `${arrowSize}px solid transparent`
    arrow.style.borderRight = `${arrowSize}px solid ${arrowColor}`
  }
}

// --------------------------------------------------------------------------
// Directive Definition
// --------------------------------------------------------------------------
const followTooltip: ObjectDirective = {
  beforeMount(el: ElWithTooltip, binding) {
    const config = normalize(binding.value)
    // Create tooltip element
    const tooltip = document.createElement('div')
    tooltip.className = `v-follow-tooltip ${config.classes}`

    // Create a separate container for the tooltip content.
    const contentContainer = document.createElement('div')
    contentContainer.className = 'v-follow-tooltip-content'
    applyContent(contentContainer, config)
    tooltip.appendChild(contentContainer)

    Object.assign(tooltip.style, baseTooltipStyle())
    document.body.appendChild(tooltip)

    // Create arrow element and append it to the tooltip.
    const arrow = document.createElement('div')
    arrow.className = 'v-follow-tooltip-arrow'
    tooltip.appendChild(arrow)

    const hide = (e?: MouseEvent) => {
      // Optionally, if e exists and is in the ignore zone, we can proceed to hide.
      if (e && shouldIgnoreEvent(e, config.ignoreSelector)) return
      if (!el._followTooltip || !el._followTooltip.config) return
      tooltip.style.opacity = '0'
      setTimeout(() => {
        tooltip.style.display = 'none'
      }, 200)
    }

    // Define the move handler.
    const move = (e: MouseEvent) => {
      // If the event target matches the ignoreSelector, hide the tooltip.
      if (shouldIgnoreEvent(e, config.ignoreSelector)) {
        hide()
        return
      }
      if (!el._followTooltip || !el._followTooltip.config) return
      if (!el._followTooltip.config.enabled) return
      const { x, y } = calculatePosition(e, tooltip, el._followTooltip.config)
      tooltip.style.left = `${x}px`
      tooltip.style.top = `${y}px`
      const effectivePlacement = getEffectivePlacement(e, tooltip, el._followTooltip.config)
      updateArrow(tooltip, effectivePlacement)
    }
    // Wrap move in a throttle.
    const throttledMove = throttle(move, 1)

    const show = (e: MouseEvent) => {
      if (shouldIgnoreEvent(e, config.ignoreSelector)) {
        hide()
        return
      }
      if (!el._followTooltip || !el._followTooltip.config) return
      if (!el._followTooltip.config.enabled) return
      tooltip.style.opacity = '1'
      tooltip.style.display = 'block'
      const effectivePlacement = getEffectivePlacement(
        el.getBoundingClientRect(),
        tooltip,
        el._followTooltip.config
      )
      updateArrow(tooltip, effectivePlacement)
      if (!el._followTooltip.config.follow) {
        const { x, y } = calculatePosition(
          el.getBoundingClientRect(),
          tooltip,
          el._followTooltip.config
        )
        tooltip.style.left = `${x}px`
        tooltip.style.top = `${y}px`
      }
    }

    el._followTooltip = {
      tooltip,
      content: contentContainer,
      move: throttledMove,
      show,
      hide,
      config,
    }
    if (config.follow) el.addEventListener('mousemove', throttledMove)
    el.addEventListener('mouseenter', show)
    el.addEventListener('mouseover', show)
    el.addEventListener('mouseleave', hide)
  },

  updated(el: ElWithTooltip, binding) {
    const tooltip = el._followTooltip?.tooltip
    if (!tooltip) return
    const config = normalize(binding.value)
    el._followTooltip!.config = config
    tooltip.className = `v-follow-tooltip ${config.classes}`
    const contentContainer = el._followTooltip!.content
    if (contentContainer) {
      applyContent(contentContainer, config)
    }
  },

  unmounted(el: ElWithTooltip) {
    const handlers = el._followTooltip
    if (!handlers) return
    const { tooltip, move, show, hide, config } = handlers
    if (config.follow) el.removeEventListener('mousemove', move)
    el.removeEventListener('mouseenter', show)
    el.removeEventListener('mouseleave', hide)
    tooltip?.remove()
    delete el._followTooltip
  },
}

export default followTooltip
